import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  createSuperAdminPackage,
  getSuperAdminPackage,
  getSuperAdminPackageModules,
  SuperAdminPackageModuleOption,
  updateSuperAdminPackage,
} from "../../services/superAdminApi";

const toModulesArray = (value: any): string[] => {
  if (!value) return [];
  if (Array.isArray(value)) return value.map((x) => String(x));
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed.map((x) => String(x)) : [];
    } catch {
      return [];
    }
  }
  return [];
};

const isTrue = (value?: string | null) => {
  const v = (value || "").toLowerCase();
  return v === "true" || v === "yes";
};

type FieldProps = {
  label: string;
  children: React.ReactNode;
};

const Field: React.FC<FieldProps> = ({ label, children }) => (
  <label className="block">
    <span className="mb-1 block text-sm font-semibold text-[#402B69]">{label}</span>
    {children}
  </label>
);

const SuperAdminPackageForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [modules, setModules] = useState<SuperAdminPackageModuleOption[]>([]);
  const [isTrialPackage, setIsTrialPackage] = useState(false);
  const [form, setForm] = useState<Record<string, any>>({
    name: "",
    max_employees: "",
    max_services: "",
    max_products: "",
    max_customers: "",
    max_deals: "",
    max_roles: "",
    no_of_days: "",
    monthly_price: "",
    annual_price: "",
    stripe_monthly_plan_id: "",
    stripe_annual_plan_id: "",
    razorpay_monthly_plan_id: "",
    razorpay_annual_plan_id: "",
    description: "",
    status: "inactive",
    make_private: false,
    mark_recommended: false,
    package_modules: [] as string[],
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        const moduleOptions = await getSuperAdminPackageModules();
        setModules(moduleOptions || []);

        if (isEditMode && id) {
          const pkg = await getSuperAdminPackage(Number(id));
          const trialPackage = String(pkg.type || "").toLowerCase() === "trial" || Number(id) === 1;
          setIsTrialPackage(trialPackage);
          setForm((prev) => ({
            ...prev,
            name: pkg.name || "",
            max_employees: pkg.max_employees ?? "",
            max_services: pkg.max_services ?? "",
            max_products: pkg.max_products ?? "",
            max_customers: pkg.max_customers ?? "",
            max_deals: pkg.max_deals ?? "",
            max_roles: pkg.max_roles ?? "",
            no_of_days: pkg.no_of_days ?? "",
            monthly_price: pkg.monthly_price ?? "",
            annual_price: pkg.annual_price ?? "",
            stripe_monthly_plan_id: pkg.stripe_monthly_plan_id || "",
            stripe_annual_plan_id: pkg.stripe_annual_plan_id || "",
            razorpay_monthly_plan_id: pkg.razorpay_monthly_plan_id || "",
            razorpay_annual_plan_id: pkg.razorpay_annual_plan_id || "",
            description: pkg.description || "",
            status: pkg.status || "inactive",
            make_private: isTrue(pkg.make_private),
            mark_recommended: isTrue(pkg.mark_recommended),
            package_modules: toModulesArray(pkg.package_modules),
          }));
        }
      } catch (e: any) {
        setError(e?.response?.data?.message || "Failed to load package form.");
      }
    };
    loadData();
  }, [id, isEditMode]);

  const allSelected = useMemo(() => {
    if (modules.length === 0) return false;
    return modules.every((m) => form.package_modules.includes(m.name));
  }, [modules, form.package_modules]);

  const canSubmit = useMemo(() => {
    return (
      form.name &&
      form.max_employees !== "" &&
      form.max_services !== "" &&
      form.max_products !== "" &&
      form.max_customers !== "" &&
      form.max_deals !== "" &&
      form.max_roles !== "" &&
      (!isTrialPackage || form.no_of_days !== "") &&
      form.monthly_price !== "" &&
      form.annual_price !== ""
    );
  }, [form, isTrialPackage]);

  const toggleModule = (name: string) => {
    setForm((prev: Record<string, any>) => {
      const current: string[] = prev.package_modules || [];
      const exists = current.includes(name);
      return {
        ...prev,
        package_modules: exists ? current.filter((x) => x !== name) : [...current, name],
      };
    });
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      setSubmitting(true);
      setError("");
      setSuccess("");

      const payload = {
        name: form.name,
        max_employees: Number(form.max_employees),
        max_services: Number(form.max_services),
        max_products: Number(form.max_products),
        max_customers: Number(form.max_customers),
        max_deals: Number(form.max_deals),
        max_roles: Number(form.max_roles),
        no_of_days: isTrialPackage ? Number(form.no_of_days) : undefined,
        monthly_price: Number(form.monthly_price),
        annual_price: Number(form.annual_price),
        stripe_monthly_plan_id: form.stripe_monthly_plan_id || undefined,
        stripe_annual_plan_id: form.stripe_annual_plan_id || undefined,
        razorpay_monthly_plan_id: form.razorpay_monthly_plan_id || undefined,
        razorpay_annual_plan_id: form.razorpay_annual_plan_id || undefined,
        description: form.description || undefined,
        status: form.status || "inactive",
        make_private: form.make_private ? "true" : "false",
        mark_recommended: form.mark_recommended ? "true" : "false",
        package_modules: form.package_modules || [],
      };

      if (isEditMode && id) {
        await updateSuperAdminPackage(Number(id), payload);
      } else {
        await createSuperAdminPackage(payload);
      }

      const successMessage = isEditMode ? "Package updated successfully." : "Package created successfully.";
      setSuccess(successMessage);
      toast.success(successMessage);
      setTimeout(() => navigate("/superadmin/packages"), 600);
    } catch (e: any) {
      setError(e?.response?.data?.message || "Failed to save package.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-5 p-1">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-[#402B69]">
            {isEditMode ? "Edit Package" : "Add Package"}
          </h1>
          <p className="text-sm text-gray-500">Web-aligned package form and modules selection.</p>
        </div>
        <button
          type="button"
          onClick={() => navigate("/superadmin/packages")}
          className="rounded-lg border border-[#E5DDF5] bg-white px-4 py-2 text-sm font-semibold text-[#402B69] hover:bg-[#F7F3FF]"
        >
          Back to Packages
        </button>
      </div>

      {error ? <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div> : null}
      {success ? <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">{success}</div> : null}

      <div className="rounded-xl border border-[#E5DDF5] bg-white shadow-sm">
        <div className="border-b bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700">
          {isEditMode ? "Edit Package" : "Add Package"}
        </div>
        <div className="p-4 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Name">
              <input value={form.name} onChange={(e) => setForm((p: any) => ({ ...p, name: e.target.value }))} className="w-full rounded-lg border border-[#D5C6EE] px-3 py-2 text-sm" placeholder="Enter package name" />
            </Field>
            <Field label="Max Employees">
              <input type="number" min="0" value={form.max_employees} onChange={(e) => setForm((p: any) => ({ ...p, max_employees: e.target.value }))} className="w-full rounded-lg border border-[#D5C6EE] px-3 py-2 text-sm" placeholder="0 for unlimited" />
            </Field>
            <Field label="Max Deals">
              <input type="number" min="0" value={form.max_deals} onChange={(e) => setForm((p: any) => ({ ...p, max_deals: e.target.value }))} className="w-full rounded-lg border border-[#D5C6EE] px-3 py-2 text-sm" placeholder="0 for unlimited" />
            </Field>
            <Field label="Max Services">
              <input type="number" min="0" value={form.max_services} onChange={(e) => setForm((p: any) => ({ ...p, max_services: e.target.value }))} className="w-full rounded-lg border border-[#D5C6EE] px-3 py-2 text-sm" placeholder="0 for unlimited" />
            </Field>
            <Field label="Max Products">
              <input type="number" min="0" value={form.max_products} onChange={(e) => setForm((p: any) => ({ ...p, max_products: e.target.value }))} className="w-full rounded-lg border border-[#D5C6EE] px-3 py-2 text-sm" placeholder="0 for unlimited" />
            </Field>
            <Field label="Max Customers">
              <input type="number" min="0" value={form.max_customers} onChange={(e) => setForm((p: any) => ({ ...p, max_customers: e.target.value }))} className="w-full rounded-lg border border-[#D5C6EE] px-3 py-2 text-sm" placeholder="0 for unlimited" />
            </Field>
            <Field label="Max Roles">
              <input type="number" min="0" value={form.max_roles} onChange={(e) => setForm((p: any) => ({ ...p, max_roles: e.target.value }))} className="w-full rounded-lg border border-[#D5C6EE] px-3 py-2 text-sm" placeholder="0 for unlimited" />
            </Field>
            {isTrialPackage ? (
              <Field label="Trial No. of Days">
                <input
                  type="number"
                  min="0"
                  value={form.no_of_days}
                  onChange={(e) => setForm((p: any) => ({ ...p, no_of_days: e.target.value }))}
                  className="w-full rounded-lg border border-[#D5C6EE] px-3 py-2 text-sm"
                  placeholder="e.g. 7"
                />
              </Field>
            ) : null}
          </div>

          <div>
            <h3 className="text-sm font-semibold text-[#402B69] mb-3">Payment Gateway Plans</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Monthly Price">
                <input type="number" min="0" value={form.monthly_price} onChange={(e) => setForm((p: any) => ({ ...p, monthly_price: e.target.value }))} className="w-full rounded-lg border border-[#D5C6EE] px-3 py-2 text-sm" placeholder="0.00" />
              </Field>
              <Field label="Annual Price">
                <input type="number" min="0" value={form.annual_price} onChange={(e) => setForm((p: any) => ({ ...p, annual_price: e.target.value }))} className="w-full rounded-lg border border-[#D5C6EE] px-3 py-2 text-sm" placeholder="0.00" />
              </Field>
              <Field label="Stripe Monthly Plan ID">
                <input value={form.stripe_monthly_plan_id} onChange={(e) => setForm((p: any) => ({ ...p, stripe_monthly_plan_id: e.target.value }))} className="w-full rounded-lg border border-[#D5C6EE] px-3 py-2 text-sm" placeholder="plan_xxx" />
              </Field>
              <Field label="Stripe Yearly Plan ID">
                <input value={form.stripe_annual_plan_id} onChange={(e) => setForm((p: any) => ({ ...p, stripe_annual_plan_id: e.target.value }))} className="w-full rounded-lg border border-[#D5C6EE] px-3 py-2 text-sm" placeholder="plan_xxx" />
              </Field>
              <Field label="Razorpay Monthly Plan ID">
                <input value={form.razorpay_monthly_plan_id} onChange={(e) => setForm((p: any) => ({ ...p, razorpay_monthly_plan_id: e.target.value }))} className="w-full rounded-lg border border-[#D5C6EE] px-3 py-2 text-sm" placeholder="plan_xxx" />
              </Field>
              <Field label="Razorpay Yearly Plan ID">
                <input value={form.razorpay_annual_plan_id} onChange={(e) => setForm((p: any) => ({ ...p, razorpay_annual_plan_id: e.target.value }))} className="w-full rounded-lg border border-[#D5C6EE] px-3 py-2 text-sm" placeholder="plan_xxx" />
              </Field>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.mark_recommended}
                onChange={(e) => setForm((p: any) => ({ ...p, mark_recommended: e.target.checked }))}
              />
              Mark As Recommended
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.status === "active"}
                onChange={(e) => setForm((p: any) => ({ ...p, status: e.target.checked ? "active" : "inactive" }))}
              />
              Status (Active)
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.make_private}
                onChange={(e) => setForm((p: any) => ({ ...p, make_private: e.target.checked }))}
              />
              Make Private
            </label>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-[#402B69] mb-3">Select Modules</h3>
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 mb-2">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={(e) => {
                  if (e.target.checked) {
                    setForm((p: any) => ({ ...p, package_modules: modules.map((m) => m.name) }));
                  } else {
                    setForm((p: any) => ({ ...p, package_modules: [] }));
                  }
                }}
              />
              Select All
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {modules.map((module) => (
                <label key={module.id} className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={form.package_modules.includes(module.name)}
                    onChange={() => toggleModule(module.name)}
                  />
                  {module.name}
                </label>
              ))}
            </div>
          </div>

          <div>
            <Field label="Description">
              <textarea
                value={form.description}
                onChange={(e) => setForm((p: any) => ({ ...p, description: e.target.value }))}
                className="w-full min-h-[120px] rounded-lg border border-[#D5C6EE] px-3 py-2 text-sm"
                placeholder="Enter package description"
              />
            </Field>
          </div>

          <div>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting || !canSubmit}
              className="rounded-lg bg-[#885ABB] px-5 py-2 text-sm font-semibold text-white hover:bg-[#7748A4] disabled:opacity-50"
            >
              {submitting ? (isEditMode ? "Updating..." : "Saving...") : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminPackageForm;
